import useWebAudioPlayer from '@/mixins/useWebAudioPlayer';
import {
  Chat,
  CHAT_TYPE_DIRECT,
  CHAT_TYPE_FORUM,
  CHAT_TYPE_GROUP,
  CHAT_TYPE_PROJECT,
  ChatFilter,
  ChatGroup,
  ChatGroupMetaEntry,
  ChatMessage,
  ChatMessageType,
  ChatText,
  CLEAR_CHAT_MESSAGE_ID,
  CONCURRENT_MESSAGE_LOADING,
  GROUP_MESSAGES_PAGE_SIZE,
  MINIMAL_CONSECUTIVE_LOADING_INTERVAL_MS,
  MESSAGE_EVENT_REFRESH,
  NEW_CHAT_MESSAGE_ID,
  SendMessageResult,
  TEMPORARY_CHAT_MESSAGE_ID,
  TEMPORARY_CHAT_USER_ID
} from '@/models/Messenger';
import MessengerGroupService from '@/services/MessengerGroupService';
import { BaseUserWithRole, User } from '@/models/User';
import { cleanPreviousNewChats, createNewChatFromUser, findExistingChatForUser } from '@/state/messenger/Chat';
import eventBus from '@/state/messenger/EventBus';
import getLatestChatFromGroups from '@/state/messenger/getLatestChatFromGroups';
import { byCreatedAscending } from '@/state/messenger/messageWindow';
import useMessageWindow from '@/state/messenger/useMessageWindow';
import {
  editMessage as apiEditMessage,
  getAllChats,
  markAsRead as apiMarkAsRead,
  removeChats,
  sendMessage as apiSendMessage
} from '@/state/messenger/Network';
import { traverseMessagesInChats, traverseMessagesInGroups } from '@/state/messenger/traverseHelpers';
import useFilter from '@/state/messenger/useFilter';
import useGrouper from '@/state/messenger/useGrouper';
import { getChatUnreadMessageCount, getGroupsUnreadMessageCount, getLatestUnreadChat } from '@/state/messenger/useGroupsUnreadMessages';
import useSorter from '@/state/messenger/useSorter';
import asyncWait from '@/utils/asyncWait';
import { logError } from '@/utils/logger';
import {
  computed, ComputedRef, nextTick, reactive, Ref, ref, watch
} from 'vue';
import {
  createGlobalState, useDocumentVisibility, useIntervalFn
} from '@vueuse/core';
import useSparseLocalStorage from '@/mixins/useSparseLocalStorage';
import { PromisePool } from '@supercharge/promise-pool';
import { debounce } from 'lodash';
import { REFRESH_INTERVAL_MESSENGER_BEGINNING } from '@/models/IntervalTimes';

const useMessengerState = createGlobalState(() => {
  const isOpened = ref<boolean>(false);
  const isExpanded = ref<boolean>(false);
  const isLoading = ref<boolean>(false);
  const isLoadingFirstTime = ref<boolean>(true);
  const searchTerm = ref<string>('');
  const showNewFilter = ref<boolean>(false);
  const showSortPanel = ref<boolean>(false);
  const showOptionsPanel = ref<boolean>(false);
  const showNewChatPanel = ref<boolean>(false);
  const isGroupPanelOpen = ref<boolean>(false);
  const chatgroupGroupId = ref<string | null>(null);
  const sortBy = useSparseLocalStorage('chat-sort-by', 'time');
  const sortDir = useSparseLocalStorage('chat-sort-direction', 'descending');
  const isNewChat = ref<boolean>(false);

  const isSoundEnabled = useSparseLocalStorage('soundEnabled', true);

  const isSoundTemporarilyMuted = ref<boolean>(false);
  const sendTestTone = ref<boolean>(false);
  const isFlat = useSparseLocalStorage('chat-flatmode', true);
  const groupFilters = reactive<ChatFilter>({
    all: true,
    [CHAT_TYPE_DIRECT]: false,
    [CHAT_TYPE_FORUM]: false,
    [CHAT_TYPE_PROJECT]: false,
    [CHAT_TYPE_GROUP]: false
  });
  const selectedChatId = ref<string>('');
  const editMessageId = ref<string>('');
  const sendingMessageId = ref('');
  const openedGroupIDs = reactive({
    openedIds: []
  }) as {
    openedIds: string[];
  };
  const isForumGroupOpened = ref(true);
  const isProjectGroupOpened = ref(true);
  const isChatGroupOpened = ref(true);
  const chatTexts = ref([]) as Ref<ChatText[]>;
  const chats = ref<Chat[]>([]);
  const chatgroups = ref<ChatGroup[]>([]);
  const groupMessages = ref<ChatMessage[]>([]);
  // Last message per group (by groupId) for the chat-list sort/preview — the group
  // list endpoint carries no message/time, so we load it like the 1:1 chats do.
  const groupLastMessages = ref<Record<string, ChatMessage | undefined>>({});
  // Per-group unread/message counts from the groups/meta endpoint (keyed by groupId).
  const perGroupMeta = ref<Record<string, ChatGroupMetaEntry>>({});
  const isWaitingForData = ref(true);

  const isInputFocussed = ref<boolean>(false);
  const isInputMaximized = ref<boolean>(false);
  const isMarkingChats = ref<boolean>(false);
  const markedChats = ref<string[]>([]);

  const isOpenedGroupSelectedOnStartup = ref<boolean>(false);

  const isAnythingInsideSidebarBox = ref<boolean>(false);

  const documentVisibility = useDocumentVisibility();

  const {
    resume: resumeMetaPolling,
    pause: pauseMetaPolling
  } = useIntervalFn(() => {
    eventBus.emit(MESSAGE_EVENT_REFRESH);
  }, REFRESH_INTERVAL_MESSENGER_BEGINNING, {
    immediate: false
  });

  watch(isMarkingChats, (is) => {
    if (!is) markedChats.value = [];
  });

  watch(selectedChatId, (chatId) => {
    if (chatId) {
      editMessageId.value = '';
      // changing chat will start an extra interval for meta polling and make an initial refresh
      eventBus.emit(MESSAGE_EVENT_REFRESH);
      resumeMetaPolling();
    } else {
      pauseMetaPolling();
    }
  });

  // Loads only track unread flags; the read POST happens in markGroupSeen
  // once the newest message was really seen.
  const { trackUnread, markGroupSeen } = (() => {
    const groupFadeTimers: Record<string, ReturnType<typeof setTimeout>> = {};
    const groupUnreadIds: Record<string, Set<string>> = {};
    const markingSeen: Record<string, boolean> = {};

    const trackUnread = (chatId: string) => {
      const msgs = groupMessages.value;
      const stillUnread = groupUnreadIds[chatId] ?? new Set<string>();
      groupUnreadIds[chatId] = stillUnread;
      msgs.filter((m) => !m.read).forEach((m) => stillUnread.add(m.messageId));
      msgs.forEach((m) => {
        if (stillUnread.has(m.messageId)) m.read = false;
      });
    };

    const markGroupSeen = async (chatId: string, isStillSeen: () => boolean = () => true) => {
      const hasUnread = (perGroupMeta.value[chatId]?.unreadCount ?? 0) > 0
        || (groupUnreadIds[chatId]?.size ?? 0) > 0;
      if (markingSeen[chatId] || !hasUnread) return;
      markingSeen[chatId] = true;
      try {
        // Dwell time: counts as seen only if still in view afterwards.
        await asyncWait(1000);
        if (selectedChatId.value !== chatId || !isStillSeen()) return;
        try {
          await MessengerGroupService.markGroupRead(chatId);
        } catch (error) {
          logError(error);
          return;
        }
        groupUnreadIds[chatId]?.clear();
        // Badge to 0 now; the next meta load re-derives it.
        const meta = perGroupMeta.value[chatId];
        if (meta) meta.unreadCount = 0;
        if (groupFadeTimers[chatId] === undefined) {
          groupFadeTimers[chatId] = setTimeout(() => {
            delete groupFadeTimers[chatId];
            if (selectedChatId.value === chatId) {
              // Messages that arrived after the POST stay unread until seen.
              groupMessages.value.forEach((m) => {
                if (!groupUnreadIds[chatId]?.has(m.messageId)) m.read = true;
              });
            }
          }, 3000);
        }
      } finally {
        markingSeen[chatId] = false;
      }
    };

    return { trackUnread, markGroupSeen };
  })();

  // Paged window over the open group's messages; older pages are history and
  // bypass the unread handling.
  const groupWindow = useMessageWindow({
    messages: groupMessages,
    pageSize: GROUP_MESSAGES_PAGE_SIZE,
    fetchPage: (chatId, params) => MessengerGroupService.getGroupMessages(chatId, params),
    currentChatId: () => selectedChatId.value,
    exists: (chatId) => chatgroups.value.some((g) => g.groupId === chatId),
    onApplied: trackUnread
  });

  const setGroupPreview = (groupId: string) => {
    // Temps never carry the preview (unsent text, no real creator).
    const lastReal = [...groupMessages.value].reverse().find((m) => m.creator !== TEMPORARY_CHAT_USER_ID);
    if (!lastReal) return;
    groupLastMessages.value = {
      ...groupLastMessages.value,
      [groupId]: lastReal
    };
  };

  // Optimistic send: a temporary message until the server echo arrives.
  let groupTempIdCounter = 0;

  const insertTemporaryGroupMessage = (text: string): ChatMessage => {
    // A new attempt replaces previous failed sends.
    const rest = groupMessages.value.filter((m) => !m.hasFailedSending);
    const newest = rest[rest.length - 1];
    const now = new Date().toISOString();
    const temp: ChatMessage = {
      messageId: `${TEMPORARY_CHAT_MESSAGE_ID}g${groupTempIdCounter += 1}`,
      text,
      // Clamp against clock skew so the temp always sorts last.
      created: newest && newest.created >= now
        ? new Date(new Date(newest.created).getTime() + 1).toISOString()
        : now,
      creator: TEMPORARY_CHAT_USER_ID,
      read: true,
      edited: false,
      flagged: false,
      subMessages: [],
      upvoteCount: 0,
      downvoteCount: 0,
      voteResult: 0,
      messageType: ChatMessageType.PRIVATE_MESSAGING_MESSAGE
    };
    groupMessages.value = [...rest, temp];
    return temp;
  };

  const resolveTemporaryGroupMessage = (groupId: string, tempId: string, message: ChatMessage) => {
    // Never regress the preview behind a newer foreign message.
    const currentPreview = groupLastMessages.value[groupId];
    if (!currentPreview || currentPreview.created <= message.created) {
      groupLastMessages.value = { ...groupLastMessages.value, [groupId]: message };
    }
    if (selectedChatId.value !== groupId) return;
    // The poll may have merged the real message already.
    const rest = groupMessages.value.filter((m) => m.messageId !== tempId && m.messageId !== message.messageId);
    groupMessages.value = [...rest, message].sort(byCreatedAscending);
  };

  const failTemporaryGroupMessage = (groupId: string, temp: ChatMessage) => {
    if (selectedChatId.value !== groupId) return;
    // Re-insert if a merge/reset dropped the temp, so the text stays retryable.
    const failed = { ...temp, hasFailedSending: true };
    const exists = groupMessages.value.some((m) => m.messageId === temp.messageId);
    groupMessages.value = exists
      ? groupMessages.value.map((m) => (m.messageId === temp.messageId ? failed : m))
      : [...groupMessages.value, failed].sort(byCreatedAscending);
  };

  const patchGroupMessage = (groupId: string, message: ChatMessage) => {
    if (selectedChatId.value === groupId) {
      groupMessages.value = groupMessages.value.map((m) => (m.messageId === message.messageId ? message : m));
    }
    if (groupLastMessages.value[groupId]?.messageId === message.messageId) {
      groupLastMessages.value = { ...groupLastMessages.value, [groupId]: message };
    }
  };

  watch(selectedChatId, (chatId) => groupWindow.open(chatId));

  // User leaves or enters tab => no more regular polling ever again (only via overview request then).
  watch(documentVisibility, (visibility) => {
    if (visibility === 'hidden') pauseMetaPolling();
    else if (selectedChatId.value) resumeMetaPolling();
  });

  const getChatById = (chatId: string) => chats.value.find(({ id }) => id === chatId);
  const getChatTextById = (chatId: string) => chatTexts.value.find(({ id }) => id === chatId);
  const getMessageById = (id: string): ChatMessage | null => {
    const groupMessage = groupMessages.value.find((message) => message.messageId === id);
    if (groupMessage) return groupMessage;
    let foundMessage = null;
    traverseMessagesInChats(chats.value, (message) => {
      if (message.messageId === id) {
        foundMessage = message;
        return true;
      }
      return false;
    });
    return foundMessage;
  };

  // computed

  const buildGroupChat = (group: ChatGroup, messages: ChatMessage[]): Chat => ({
    type: CHAT_TYPE_GROUP,
    id: group.groupId,
    chatId: group.groupId,
    messages,
    privatePartner: { firstname: group.title } as User
  });

  const selectedChat = computed(() => {
    const normalChat = getChatById(selectedChatId.value);
    if (normalChat) return normalChat;
    const group = chatgroups.value.find((g) => g.groupId === selectedChatId.value);
    if (group) return buildGroupChat(group, groupMessages.value);
    return undefined;
  }) as ComputedRef<Chat>;
  const selectedChatText = computed(() => getChatTextById(selectedChatId.value)?.text ?? '');

  const setSelectedChatText = (text: string) => {
    const chatText = getChatTextById(selectedChatId.value);
    if (chatText) {
      chatText.text = text;
    } else {
      chatTexts.value.push({
        text,
        id: selectedChatId.value
      });
    }
  };

  // GROUPING, FILTERING, SORTING

  const { groups } = useGrouper(chats);
  const { groupsFiltered, chatsFiltered } = useFilter(groups, chats, {
    showNew: showNewFilter,
    searchTerm,
    filters: groupFilters
  });
  const { groupsFilteredSorted, chatsSorted } = useSorter(groupsFiltered, chatsFiltered, {
    sortBy,
    sortDir
  });
  const chatgroupsUnreadMessages = computed(() => Object.values(perGroupMeta.value)
    .reduce((prev, meta) => prev + (meta?.unreadCount ?? 0), 0));
  const unreadMessages = computed(() => getGroupsUnreadMessageCount(groups.value) + chatgroupsUnreadMessages.value);
  const unfilteredUnreadMessages = computed(() => chats.value.reduce((prev, chat) => prev + getChatUnreadMessageCount(chat), 0)
    + chatgroupsUnreadMessages.value);

  // Sound
  // eslint-disable-next-line global-require
  const newMessageUrl = require('@/assets/audio/newMessage.mp3');

  const { play: _playAudio, init: initAudio } = useWebAudioPlayer();

  const playAudio = async () => {
    if (isSoundEnabled.value && !isSoundTemporarilyMuted.value) await _playAudio();
  };

  initAudio(newMessageUrl).then();

  watch(sendTestTone, (is) => {
    if (is) playAudio();
    sendTestTone.value = false;
  });

  watch(unfilteredUnreadMessages, (cur, prev) => {
    if (cur > prev) playAudio();
  });
  // ACTIONS

  const loadChatgroups = async () => {
    const [groups, metaList] = await Promise.all([
      MessengerGroupService.getGroups(),
      MessengerGroupService.getGroupsMeta()
    ]);
    chatgroups.value = groups;

    const previousMeta = perGroupMeta.value;
    const previousLastMessages = groupLastMessages.value;
    const meta = Object.fromEntries(metaList.map((m) => [m.groupId, m] as const));
    perGroupMeta.value = meta;

    // The group list carries no message/time, so the last message (for the list
    // sort/preview) comes from the messages endpoint — but only for groups whose
    // messageCount changed, the same meta diffing getAllChats does for 1:1 chats.
    const lastMessages: Record<string, ChatMessage | undefined> = {};
    await PromisePool
      .for(groups)
      .withConcurrency(CONCURRENT_MESSAGE_LOADING)
      .process(async ({ groupId }) => {
        // The open group's messages are loaded separately — don't fetch them twice.
        const isOpen = groupId === selectedChatId.value;
        const unchanged = groupId in previousLastMessages
          && meta[groupId]?.messageCount === previousMeta[groupId]?.messageCount;
        if (isOpen || unchanged) {
          lastMessages[groupId] = previousLastMessages[groupId];
          return;
        }
        const { messages: [newest] } = await MessengerGroupService.getGroupMessages(groupId, { limit: 1 });
        lastMessages[groupId] = newest;
      });
    groupLastMessages.value = lastMessages;
  };

  let lastChatgroupsLoadingTime = 0;
  const loadChatgroupsThrottled = async () => {
    const now = new Date().getTime();
    if (lastChatgroupsLoadingTime + MINIMAL_CONSECUTIVE_LOADING_INTERVAL_MS > now) return;
    lastChatgroupsLoadingTime = now;
    await loadChatgroups();
  };

  const loadChatsImmediately = async () => {
    const allChats = await getAllChats();
    if (allChats) {
      chats.value = allChats;
      isLoadingFirstTime.value = false;
    }
    await loadChatgroupsThrottled();

    const groupId = selectedChatId.value;
    if (groupId && chatgroups.value.some((g) => g.groupId === groupId)) {
      if (await groupWindow.refreshNewest(groupId)) setGroupPreview(groupId);
    }
  };

  const loadChats = debounce(async () => {
    // prevent refreshing when currently writing a new message:
    if (selectedChatId.value !== NEW_CHAT_MESSAGE_ID && !isMarkingChats.value) {
      isLoading.value = true;
      await loadChatsImmediately();
      isLoading.value = false;
      isWaitingForData.value = false;
    }
  }, 50);

  const startNewChat = () => {
    isGroupPanelOpen.value = false;
    chatgroupGroupId.value = null;
    isNewChat.value = true;
    if (!isOpened.value) isOpened.value = true;
  };

  const openChatgroupPanel = (groupId: string | null = null) => {
    isNewChat.value = false;
    chatgroupGroupId.value = groupId;
    isGroupPanelOpen.value = true;
    if (!isOpened.value) isOpened.value = true;
  };

  const closeChatgroupPanel = () => {
    isGroupPanelOpen.value = false;
    chatgroupGroupId.value = null;
    loadChatgroups();
    if (selectedChatId.value) groupWindow.refreshNewest(selectedChatId.value);
  };

  const joinChatGroup = async (groupId: string) => {
    if (!groupId) return;
    await MessengerGroupService.joinGroup(groupId);
    await loadChatgroups();
    selectedChatId.value = groupId;
  };

  const createNewChat = async (user: BaseUserWithRole, messageText = '') => {
    if (isGroupPanelOpen.value) closeChatgroupPanel();
    if (!chats.value?.length) {
      await loadChatsImmediately();
    }
    const existingChat = findExistingChatForUser(chats, user);

    if (!existingChat) {
      cleanPreviousNewChats(chats);
      chats.value.push(createNewChatFromUser(user));
      selectedChatId.value = NEW_CHAT_MESSAGE_ID;
    } else {
      selectedChatId.value = CLEAR_CHAT_MESSAGE_ID;
      await nextTick();
      selectedChatId.value = existingChat.id;
    }

    if (messageText) {
      const msgId = editMessageId.value;
      setSelectedChatText(messageText);
      editMessageId.value = CLEAR_CHAT_MESSAGE_ID;
      await nextTick();
      editMessageId.value = msgId;
    }
    isNewChat.value = false;
  };

  const selectChat = (chatId: string) => {
    if (chatId) {
      if (isGroupPanelOpen.value) closeChatgroupPanel();
      selectedChatId.value = chatId === selectedChatId.value && !isExpanded.value ? '' : chatId;
    } else if (!isExpanded.value) {
      selectedChatId.value = '';
    }
    isNewChat.value = false;
  };

  const openUnreadGroups = () => {
    traverseMessagesInGroups(groups.value, (message, chat, group) => {
      if (!message.read && group?.id && !openedGroupIDs.openedIds.includes(group.id)) {
        // console.log('Add to opened groups:', group.id);
        openedGroupIDs.openedIds.push(group.id);
      }
    });
  };

  // when chats are loaded for the first time, open all groups with unread messages:
  watch(chats, (cur, prev) => {
    if (!prev?.length) {
      nextTick(() => openUnreadGroups());
    }
  });

  // API Communication:

  const removeMarkedChats = async () => {
    await removeChats(markedChats.value);
    isMarkingChats.value = false;
    eventBus.emit(MESSAGE_EVENT_REFRESH);
  };

  const sendMessage = async (text: string, messageType: ChatMessageType, isDelayed = false): Promise<SendMessageResult> => {
    if (selectedChat.value) {
      if (selectedChat.value.type === CHAT_TYPE_GROUP) {
        const groupId = selectedChat.value.id;
        const temp = insertTemporaryGroupMessage(text);
        try {
          const message = await MessengerGroupService.postGroupMessage(groupId, text);
          resolveTemporaryGroupMessage(groupId, temp.messageId, message);
          return SendMessageResult.OK;
        } catch (error) {
          logError(error);
          failTemporaryGroupMessage(groupId, temp);
          return SendMessageResult.UNKNOWN;
        }
      }
      sendingMessageId.value = selectedChatId.value;
      const result = await apiSendMessage(selectedChat.value, text, messageType);

      sendingMessageId.value = '';
      if (result?.error) {
        switch (result?.error.code) {
          case SendMessageResult.PROJECT_CLOSED:
            return result?.error.code as SendMessageResult;
          default:
            return SendMessageResult.UNKNOWN;
        }
      }
      if (!result) return SendMessageResult.UNKNOWN;

      if (selectedChatId.value === NEW_CHAT_MESSAGE_ID) {
        // await loadChatsImmediately();
        selectedChatId.value = result?.externalId ?? '';
      }

      if (isDelayed) await asyncWait(2000);
      else await nextTick();

      eventBus.emit(MESSAGE_EVENT_REFRESH);

      return SendMessageResult.OK;
    }
    throw new Error('No chat selected!');
  };

  const editMessage = async (text: string) => {
    if (editMessageId.value) {
      if (selectedChat.value?.type === CHAT_TYPE_GROUP) {
        const groupId = selectedChat.value.id;
        sendingMessageId.value = editMessageId.value;
        try {
          const message = await MessengerGroupService.editGroupMessage(groupId, editMessageId.value, text);
          patchGroupMessage(groupId, message);
        } catch (error) {
          logError(error);
          return SendMessageResult.UNKNOWN;
        } finally {
          sendingMessageId.value = '';
        }
        return SendMessageResult.OK;
      }
      sendingMessageId.value = editMessageId.value;
      const result = await apiEditMessage(text, selectedChat.value?.chatId ?? '', editMessageId.value, selectedChat.value.privatePartner.userId ?? '', selectedChat.value);

      if (!result) {
        return SendMessageResult.UNKNOWN;
      }

      await nextTick();
      eventBus.emit(MESSAGE_EVENT_REFRESH);
      sendingMessageId.value = '';

      return SendMessageResult.OK;
    } throw new Error('No message ID set!');
  };

  const markAsRead = async (message: ChatMessage) => {
    await asyncWait(1000);
    message.read = await apiMarkAsRead(message.messageId);
  };

  const watchIsExpandedToSelectNewestChat = () => {
    watch(isExpanded, (is) => {
      if (is && !selectedChatId.value && groupsFiltered) {
        const { latestChatId, latestGroupId } = getLatestChatFromGroups(groupsFiltered.value);
        selectedChatId.value = latestChatId;
        openedGroupIDs.openedIds.push(latestGroupId);
      }
    });
  };

  const preventInputUnFocusOnce = ref<boolean>(false);

  const openLatestUnreadMessage = () => {
    const latestUnreadChat = getLatestUnreadChat(chats.value);
    if (latestUnreadChat) {
      selectedChatId.value = latestUnreadChat.id;
    }
  };

  return {
    isOpened,
    isExpanded,
    isInputFocussed,
    isInputMaximized,
    isNewChat,
    isWaitingForData,
    isMarkingChats,
    isLoading,
    isLoadingFirstTime,
    isOpenedGroupSelectedOnStartup,
    isFlat,
    isAnythingInsideSidebarBox,
    isSoundTemporarilyMuted,
    markedChats,
    searchTerm,
    showNewFilter,
    sortBy,
    sortDir,
    groupFilters,
    selectedChatId,
    selectedChat,
    selectedChatText,
    openedGroupIDs,
    isForumGroupOpened,
    isProjectGroupOpened,
    isChatGroupOpened,
    chats,
    chatgroups,
    groupLastMessages,
    perGroupMeta,
    groupHasMore: groupWindow.hasMore,
    isLoadingOlderMessages: groupWindow.isLoadingOlder,
    loadOlderGroupMessages: groupWindow.loadOlder,
    groupWindowChatId: groupWindow.appliedChatId,
    markGroupSeen,
    loadChatgroups,
    chatsSorted,
    groupsFiltered,
    groupsFilteredSorted,
    unreadMessages,
    editMessageId,
    eventBus,
    preventInputUnFocusOnce,
    loadChats,
    showSortPanel,
    showOptionsPanel,
    showNewChatPanel,
    isGroupPanelOpen,
    chatgroupGroupId,
    openChatgroupPanel,
    closeChatgroupPanel,
    joinChatGroup,
    isSoundEnabled,
    sendTestTone,
    removeMarkedChats,
    startNewChat,
    createNewChat,
    selectChat,
    setSelectedChatText,
    watchIsExpandedToSelectNewestChat,
    sendMessage,
    editMessage,
    markAsRead,
    getChatById,
    getMessageById,
    openLatestUnreadMessage,
    sendingMessageId
  };
});

export default useMessengerState;
